// Compliance Score Display Component
// Shows compliance score breakdown with grade and recommendations

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, TrendingUp, TrendingDown, MapPin } from 'lucide-react';
import Modal from '../ui/Modal';
import './ComplianceScoreDisplay.css';

const ComplianceScoreDisplay = ({ isOpen, onClose, complianceData, taskData }) => {
  const { t } = useTranslation(['tasks', 'common']);
  if (!isOpen || !complianceData) return null;
  
  const { overall_score, breakdown, grade, recommendations } = complianceData;
  const ctx = taskData?.context || {};
  const locationText = [ctx.departmentName, ctx.roomName].filter(Boolean).join(' → ');
  const protocolNames = (taskData?.protocols || []).map(p => p.protocolName || p.name).filter(Boolean);
  const stepsWithErrors = [];
  for (const protocol of taskData?.protocols || []) {
    const pName = protocol.protocolName || protocol.name || 'Protocol';
    for (const step of protocol.steps || []) {
      if (step.completedWithErrors && step.incompleteActionNames?.length) {
        stepsWithErrors.push({ protocolName: pName, stepName: step.stepName || 'Step', actions: step.incompleteActionNames });
      }
    }
  }
  
  const getProgressColor = (score) => {
    if (score >= 90) return '#28a745';
    if (score >= 80) return '#5cb85c';
    if (score >= 70) return '#ffc107';
    return '#dc3545';
  };
  
  const getProgressWidth = (score) => {
    return `${Math.min(score, 100)}%`;
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Task Compliance Report"
      size="medium"
    >
      <div className="compliance-score-display">
        {/* Protocol name - prominent */}
        {protocolNames.length > 0 && (
          <div className="compliance-protocol-section">
            <h2 className="compliance-protocol-name">{protocolNames.join(' / ')}</h2>
            {locationText && (
              <div className="compliance-location">
                <MapPin size={16} />
                <span>{locationText}</span>
              </div>
            )}
          </div>
        )}

        {/* Overall Score */}
        <div className="overall-score-section">
          <div className="overall-score-header">
            <div className="score-value">
              <span className="score-number">{overall_score}%</span>
              <span className="score-grade" style={{ color: grade.color }}>
                {grade.grade}
              </span>
            </div>
            <div className="score-label" style={{ color: grade.color }}>
              {grade.label}
            </div>
          </div>
          <div className="score-progress-bar">
            <div 
              className="score-progress-fill"
              style={{ 
                width: getProgressWidth(overall_score),
                backgroundColor: getProgressColor(overall_score)
              }}
            />
          </div>
        </div>
        
        {/* Score Breakdown */}
        <div className="score-breakdown">
          <h4>Score Breakdown</h4>
          
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>Timing</span>
              <span className="breakdown-score">{breakdown.timing_score}%</span>
            </div>
            <div className="breakdown-progress-bar">
              <div 
                className="breakdown-progress-fill"
                style={{ 
                  width: getProgressWidth(breakdown.timing_score),
                  backgroundColor: getProgressColor(breakdown.timing_score)
                }}
              />
            </div>
          </div>
          
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>Completeness</span>
              <span className="breakdown-score">{breakdown.completeness_score}%</span>
            </div>
            <div className="breakdown-progress-bar">
              <div 
                className="breakdown-progress-fill"
                style={{ 
                  width: getProgressWidth(breakdown.completeness_score),
                  backgroundColor: getProgressColor(breakdown.completeness_score)
                }}
              />
            </div>
          </div>
          
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>Accuracy</span>
              <span className="breakdown-score">{breakdown.accuracy_score}%</span>
            </div>
            <div className="breakdown-progress-bar">
              <div 
                className="breakdown-progress-fill"
                style={{ 
                  width: getProgressWidth(breakdown.accuracy_score),
                  backgroundColor: getProgressColor(breakdown.accuracy_score)
                }}
              />
            </div>
          </div>
          
          <div className="breakdown-item">
            <div className="breakdown-label">
              <span>Evidence</span>
              <span className="breakdown-score">{breakdown.evidence_score}%</span>
            </div>
            <div className="breakdown-progress-bar">
              <div 
                className="breakdown-progress-fill"
                style={{ 
                  width: getProgressWidth(breakdown.evidence_score),
                  backgroundColor: getProgressColor(breakdown.evidence_score)
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Steps completed with errors */}
        {stepsWithErrors.length > 0 && (
          <div className="compliance-errors-section">
            <h4>
              <AlertCircle size={18} /> {t('tasks:stepsWithErrors')}
            </h4>
            <div className="compliance-errors-list">
              {stepsWithErrors.map((item, idx) => (
                <div key={idx} className="compliance-error-item">
                  <div className="compliance-error-step">{item.protocolName} — {item.stepName}</div>
                  <div className="compliance-error-actions">
                    {t('tasks:actionsNotCompleted')}: {item.actions.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <div className="recommendations-section">
            <h4>
              <AlertCircle size={18} /> Recommendations
            </h4>
            <div className="recommendations-list">
              {recommendations.map((rec, index) => (
                <div key={index} className="recommendation-item">
                  <div className="recommendation-header">
                    <strong>{rec.area}</strong>
                    <TrendingDown size={16} className="trend-down" />
                  </div>
                  <div className="recommendation-issue">
                    <strong>Issue:</strong> {rec.issue}
                  </div>
                  <div className="recommendation-suggestion">
                    <strong>Suggestion:</strong> {rec.suggestion}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="compliance-actions">
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ComplianceScoreDisplay;



